import React, { useEffect, useState } from "react";
import { Smartphone, Download, Share, PlusSquare, Bell, BellOff, Send, X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { onPwaChange, canPromptInstall, promptInstall, isStandalone, isIOS, pushSupported, currentSubscription, enablePush, disablePush } from "../lib/pwa";

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: "var(--primary)" }}>{n}</span>
    <span className="text-sm flex items-center gap-1.5 flex-wrap">{children} {Icon && <Icon className="w-4 h-4" style={{ color: "#007AFF" }} />}</span>
  </li>
);

const InstallSection = () => {
  const [, force] = useState(0);
  useEffect(() => onPwaChange(() => force((x) => x + 1)), []);
  if (isStandalone()) {
    return <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#1B5E20" }} data-testid="pwa-installed"><CheckCircle2 className="w-4 h-4" /> App già installata su questo dispositivo</div>;
  }
  if (isIOS()) {
    return (
      <ol className="space-y-3" data-testid="pwa-ios-steps">
        <Step n={1}>Apri questa pagina con <b>Safari</b></Step>
        <Step n={2} icon={Share}>Tocca il pulsante <b>Condividi</b></Step>
        <Step n={3} icon={PlusSquare}>Scegli <b>"Aggiungi alla schermata Home"</b></Step>
        <Step n={4}>Tocca <b>Aggiungi</b>: trovi l'icona Nureal sulla Home</Step>
      </ol>
    );
  }
  if (canPromptInstall()) {
    return (
      <button className="btn-primary w-full justify-center" data-testid="pwa-install-btn"
        onClick={async () => { if (await promptInstall()) toast.success("App installata"); }}>
        <Download className="w-4 h-4" /> Installa app
      </button>
    );
  }
  return (
    <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="pwa-manual-steps">
      Apri il menu del browser (⋮ in Chrome) e scegli <b>"Installa app"</b> oppure <b>"Aggiungi a schermata Home"</b>.
    </p>
  );
};

const PushSection = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setOn(!!s)).catch(() => {}); }, []);
  if (!pushSupported()) {
    return <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="pwa-push-unsupported">
      {isIOS() && !isStandalone() ? "Su iPhone le notifiche funzionano solo dopo aver installato l'app sulla Home (iOS 16.4 o successivo). Installa l'app e riaprila dall'icona." : "Questo browser non supporta le notifiche push."}
    </p>;
  }
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (e) { toast.error(e?.response ? formatApiError(e) : e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>Ricevi un avviso per i nuovi messaggi in Comunicazioni e per i lavori nuovi o modificati della tua squadra.</p>
      <div className="flex gap-2">
        {on ? (
          <button disabled={busy} className="btn-secondary flex-1 justify-center" data-testid="pwa-push-disable"
            onClick={() => run(async () => { await disablePush(); setOn(false); }, "Notifiche disattivate")}>
            <BellOff className="w-4 h-4" /> Disattiva
          </button>
        ) : (
          <button disabled={busy} className="btn-primary flex-1 justify-center" data-testid="pwa-push-enable"
            onClick={() => run(async () => { await enablePush(); setOn(true); }, "Notifiche attivate")}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Attiva notifiche
          </button>
        )}
        {on && (
          <button disabled={busy} className="btn-secondary flex-1 justify-center" data-testid="pwa-push-test"
            onClick={() => run(() => api.post("/push/test"), "Notifica di prova inviata")}>
            <Send className="w-4 h-4" /> Prova
          </button>
        )}
      </div>
    </div>
  );
};

export const AppInstallButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="p-2 rounded-md hover:bg-black/5 transition-colors" title="App e notifiche" data-testid="pwa-open-btn">
        <Smartphone className="w-5 h-5" />
      </button>
      {open && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40" onClick={() => setOpen(false)}>
          <div className="card-shell w-full sm:max-w-md rounded-b-none sm:rounded-xl" onClick={(e) => e.stopPropagation()} data-testid="pwa-dialog" style={{ background: "#fff" }}>
            <div className="p-5 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
              <img src="/pwa/icon-192.png" alt="" className="w-10 h-10 rounded-xl" />
              <div className="flex-1">
                <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>App Nureal-Operation</h2>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>Usa il programma come un'app sul telefono</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-black/5" data-testid="pwa-dialog-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-5" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
              <section className="space-y-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Installa sul telefono</h3>
                <InstallSection />
              </section>
              <section className="space-y-3 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                <h3 className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Notifiche push</h3>
                <PushSection />
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
